import {
  Circle as CircleStyle,
  Fill,
  Stroke,
  Style,
  Text
} from 'ol/style';
import Point from 'ol/geom/Point';
import { boundingExtent, getSize as extentSize } from 'ol/extent';
import LayerUtil from '../../src/util/Layer.js';
import { OlStyleFactory as StyleFactory } from '../../src/factory/OlStyle.js'
import Vue from 'vue';

const IdsStyleFactory = {
  /** Maximum extent size in meters to detect same places for points */
  MAX_POINT_EXTENT_SIZE: 50,

  CLUSTER_FILL_COLOR: '#3399CC',

  DECLUSTER_CIRCLE_RADIUS: 40,
  DECLUSTER_CIRCLE_RADIUS_MEDIUM: 80,
  DECLUSTER_CIRCLE_RADIUS_BIG: 200,

  DEFAULT_ICON_COLOR: '#3399cc',

  LOCATION_ICON: 'location_on',

  /**
   * Creates a single point icon style.
   *
   * @param {String} fillColor
   * @returns The OL style object
   */
  createSinglePointStyle (fillColor) {
    const style = StyleFactory.createPointStyle({
      font: 'normal 30px Material Icons',
      textIcon: IdsStyleFactory.LOCATION_ICON,
      fillColor: fillColor
    });
    return style;
  },

  /**
   * Creates a selected single point icon style.
   *
   * @param {String} fillColor
   * @returns The OL style object
   */
  createSelectStyle (fillColor) {
    const style = StyleFactory.createPointStyle({
      font: 'normal 40px Material Icons',
      textIcon: IdsStyleFactory.LOCATION_ICON,
      fillColor: fillColor
    });
    return style;
  },

  /**
   * Creates a cluster style for the Ortsdaten layer.
   *
   * @param {Object} styleCache A style cache object
   *
   * @returns {ol.style.Style}
   */
  createClusterStyle (styleCache, olMap) {
    if (Vue.prototype.$appConfig.ids && Vue.prototype.$appConfig.ids.styles) {
      IdsStyleFactory.styleConf = Vue.prototype.$appConfig.ids.styles;
      IdsStyleFactory.CLUSTER_FILL_COLOR =
        IdsStyleFactory.styleConf.clusterColor;
    }

    const declusterLayer = LayerUtil.getLayerByLid('ids-decluster-layer', olMap);
    const declusterSource = declusterLayer.getSource();

    const clusterLayer = LayerUtil.getLayerByLid('ids-result-layer', olMap);
    const clusterSource = clusterLayer.getSource();

    // when cluster source changes
    // we clear the source of the twin layer
    clusterSource.on('change', () => {
      declusterSource.clear();
    });

    return function (feature, resolution) {
      const clusterFeats = feature.get('features');

      let size = 1;
      if (clusterFeats) {
        size = clusterFeats.length;
      }

      // const size = clusterFeats.length;
      const zoom = olMap.getView().getZoomForResolution(resolution);

      // check for multiple points at same place / geom
      let onlySameCoords = false;
      if (size > 1) {
        onlySameCoords = IdsStyleFactory.pointsAtSamePlace(clusterFeats);
      }

      let style = null;
      if (size === 1) {
        // render normal
        const corpusId = clusterFeats[0].get('Korpus');
        const corpusStyleConf = IdsStyleFactory.styleConf[corpusId];
        const corpusColor = corpusStyleConf
          ? corpusStyleConf.iconColor : IdsStyleFactory.DEFAULT_ICON_COLOR;

        if (clusterFeats[0].get('ids-selected')) {
          return IdsStyleFactory.createSelectStyle(corpusColor);
        } else {
          return IdsStyleFactory.createSinglePointStyle(corpusColor);
        }
      } else {
        // lot of points

        if (zoom <= 10) {
          // cluster
          // styling for real clusters
          style = styleCache[size];
          if (!style) {
            // cluster style if not existing in cache
            style = new Style({
              image: new CircleStyle({
                radius: 10,
                stroke: new Stroke({
                  color: '#fff'
                }),
                fill: new Fill({
                  color: IdsStyleFactory.CLUSTER_FILL_COLOR
                })
              }),
              text: new Text({
                text: size.toString(),
                fill: new Fill({
                  color: '#fff'
                })
              })
            });
            styleCache[size] = style;
          }
        } else {
          if (onlySameCoords) {
            // de-cluster
            // render single features de-clustered

            IdsStyleFactory.drawDeclusteredFeatures(clusterFeats, olMap, declusterLayer);

            // render original point in a decent way
            style = StyleFactory.createPointStyle({
              radius: 4,
              fillColor: 'rgba(0,91,165, .2)'
            });
          } else {
            // TODO cluster ?
          }
        }
      }

      return style;
    };
  },

  /**
   * Checks if the given point features are at the same spot, based on the a
   * threshold defined in meters in #MAX_POINT_EXTENT_SIZE.
   *
   * @param {Array<ol.Feature>} features
   * @returns true=> all points are on the same spot / false => not
   */
  pointsAtSamePlace (features) {
    // calc extent for features to check if all on same place
    const coords = features.map(f => {
      return f.getGeometry().getCoordinates()
    });
    const clusterExtent = boundingExtent(coords);
    const size = extentSize(clusterExtent);

    return size[0] < IdsStyleFactory.MAX_POINT_EXTENT_SIZE &&
      size[1] < IdsStyleFactory.MAX_POINT_EXTENT_SIZE;
  },

  /**
   * Creates a copy for every given point feature and shifts it round the
   * original coordinate in a circular layout.
   *
   * @param {Array<ol.Feature>} features Features to shift around
   * @param {ol.Map} olMap The OL map instance
   * @param {ol.layer/Vector} declusterLayer The layer to add the shifted features
   */
  drawDeclusteredFeatures (features, olMap, declusterLayer) {
    const declusterSource = declusterLayer.getSource();
    const size = features.length;
    let radius = IdsStyleFactory.DECLUSTER_CIRCLE_RADIUS;
    if (size >= 10 && size < 30) {
      radius = IdsStyleFactory.DECLUSTER_CIRCLE_RADIUS_MEDIUM;
    }
    if (size >= 30) {
      radius = IdsStyleFactory.DECLUSTER_CIRCLE_RADIUS_BIG;
    }

    // create a copy for all features and shift it round the original coordinate
    features.forEach((feature, index) => {
      // check if we have this feature already in out de-cluster
      // necessary since the drawDeclusteredFeatures fn is called multiple times
      const existing = declusterSource.getFeatures().find(decFeat => {
        return decFeat.get('originalOlUid') === feature.ol_uid;
      })
      if (existing) {
        return;
      }

      const clonedFeature = feature.clone();
      const oldGeometry = clonedFeature.getGeometry();
      const oldCoord = oldGeometry.getCoordinates();
      const newCoord = IdsStyleFactory.getCircleCoordinate(
        oldCoord,
        size,
        index,
        radius,
        olMap
      );
      const newGeometry = new Point(newCoord);

      clonedFeature.setGeometry(newGeometry);
      // add original geometry, necessary for callout lines
      clonedFeature.set('originalGeometry', oldGeometry);
      clonedFeature.set('originalOlUid', feature.ol_uid);
      clonedFeature.set('originalFeat', feature);

      const corpus = feature.get('Korpus');
      const corpusStyleConf = IdsStyleFactory.styleConf[corpus];
      const corpusColor = corpusStyleConf
        ? corpusStyleConf.iconColor : IdsStyleFactory.DEFAULT_ICON_COLOR;

      // style shifted clone and add to separate de-cluster layer
      if (feature.get('ids-selected')) {
        const selectStyle = IdsStyleFactory.createSelectStyle(corpusColor);
        clonedFeature.setStyle(selectStyle);
      } else {
        const declusterStyle = IdsStyleFactory.createSinglePointStyle(corpusColor);
        clonedFeature.setStyle(declusterStyle);
      }
      // TODO check if feature is selected, maybe needs to be store in orig feat
      declusterSource.addFeature(clonedFeature);
    });
  },

  /**
   * Compute the coordinate of a de-clustered feature.
   *
   * @param {ol.coordinate} coordinate The original coordinate of the feature
   * @param {Number} clusterCount The count of features in the current cluster
   * @param {Number} index The index of the feature in the cluster
   * @param {Number} [radius=40] The radius of the circle in pixel
   * @param {ol.Map} [radius=40] The radius of the circle in pixel
   *
   * @returns {ol.coordinate} The new coordinate in the map CRS
   */
  getCircleCoordinate (coordinate, clusterCount, index, radius, olMap) {
    if (!coordinate || !clusterCount) {
      return;
    }
    if (!index && index !== 0) {
      return;
    }

    // TODO: increase radius based on number of features
    if (!radius) {
      radius = 40;
    }

    const oldPixelCoord = olMap.getPixelFromCoordinate(coordinate);

    const maxAngle = 2 * Math.PI;
    let angle = (maxAngle / clusterCount) * (index);
    // rotate by 45°
    angle = angle + Math.PI / 4;

    const yValue = Math.sin(angle) * radius;
    const xValue = Math.cos(angle) * radius;

    oldPixelCoord[0] = oldPixelCoord[0] + xValue;
    oldPixelCoord[1] = oldPixelCoord[1] - yValue;

    return olMap.getCoordinateFromPixel(oldPixelCoord);
  }
};

export default IdsStyleFactory;



// WEBPACK FOOTER //
// ./app/ids/IdsStyleFactory.js